import { useEffect, useState, useRef } from "react";
import { Trophy, MessageSquareText, X, Award } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  useAnniversariesThisWeek,
  buildAnniversaryWaLink,
  anniversaryDayLabel,
} from "@/hooks/useAnniversariesThisWeek";

/**
 * Work Anniversary Bell — admin-topbar dropdown for upcoming DOJ milestones.
 * Auto-opens a one-time-per-session modal on first login if any in next 7 days.
 */
export default function AnniversaryBell() {
  const { user } = useAuth();
  const { rows, count, eligible, loading } = useAnniversariesThisWeek(user);
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const wrapRef = useRef(null);
  const shown = useRef(false);

  useEffect(() => {
    if (!eligible || loading || shown.current) return;
    if (count > 0) {
      // Once-per-day per device — localStorage avoids re-popping on every
      // login/navigation within the same calendar day.
      const today = new Date().toISOString().slice(0, 10);
      const key = `anniv_modal_dismissed_${today}`;
      if (!localStorage.getItem(key)) {
        setModalOpen(true);
        localStorage.setItem(key, "1");
        shown.current = true;
      } else {
        shown.current = true;
      }
    }
  }, [eligible, loading, count]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  if (!eligible) return null;

  return (
    <>
      <div className="relative" ref={wrapRef} data-testid="anniv-bell-wrap">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="relative p-2 rounded-full text-slate-600 hover:bg-amber-50 hover:text-amber-700 transition-colors"
          data-testid="anniv-bell"
          title="Work anniversaries this week"
        >
          <Trophy className="h-5 w-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1" data-testid="anniv-bell-badge">
              {count}
            </span>
          )}
        </button>
        {open && (
          <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-50" data-testid="anniv-dropdown">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4" />
                <div>
                  <div className="text-sm font-bold">Work anniversaries</div>
                  <div className="text-[10px] opacity-90">Next 7 days · {count} {count === 1 ? "person" : "people"}</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {count === 0 && (
                <div className="p-6 text-center text-xs text-slate-400">
                  <Trophy className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  No work anniversaries in the next 7 days
                </div>
              )}
              {rows.map(emp => <AnnivRow key={emp.id} emp={emp} compact />)}
            </div>
          </div>
        )}
      </div>
      {modalOpen && <AnnivModal rows={rows} count={count} onClose={() => setModalOpen(false)} />}
    </>
  );
}

function AnnivRow({ emp, compact = false }) {
  const waLink = buildAnniversaryWaLink(emp);
  const isToday = emp.days_until === 0;
  const years = emp.years_completing;
  const isMilestone = years >= 5 && years % 5 === 0; // 5, 10, 15, 20…
  return (
    <div className={`px-4 py-2.5 border-b border-slate-100 hover:bg-amber-50/30 ${isToday ? "bg-amber-50/50" : ""}`} data-testid={`anniv-row-${emp.id}`}>
      <div className="flex items-center gap-3">
        <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${isToday ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-700"}`}>
          {emp.name?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
            {emp.name}
            {isToday && <span className="text-[9px] bg-amber-500 text-white px-1 py-0.5 rounded font-bold">TODAY</span>}
            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${isMilestone ? "bg-orange-500 text-white" : "bg-amber-100 text-amber-700"}`}>
              🏆 {years}Y
            </span>
          </div>
          <div className="text-[10px] text-slate-500 truncate">
            {emp.punch_id} · {emp.designation || emp.department_name || "—"}
          </div>
          <div className="text-[10px] mt-0.5">
            <span className={isToday ? "text-amber-700 font-bold" : "text-slate-600"}>
              {anniversaryDayLabel(emp.days_until)}
            </span>
            {isMilestone && <span className="text-orange-600 font-semibold"> · Milestone!</span>}
          </div>
        </div>
        {waLink ? (
          <a href={waLink} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-colors flex-shrink-0"
              data-testid={`anniv-wish-${emp.id}`}
              title="Send appreciation">
            <MessageSquareText className="h-3 w-3" />
            {!compact && "Appreciate"}
          </a>
        ) : (
          <span className="text-[9px] text-slate-400" title="No phone on file">no #</span>
        )}
      </div>
    </div>
  );
}

function AnnivModal({ rows, count, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="anniv-modal" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 text-white px-6 py-5 relative">
          <button onClick={onClose} className="absolute top-3 right-3 text-white/80 hover:text-white" data-testid="anniv-modal-close">
            <X className="h-5 w-5" />
          </button>
          <Trophy className="h-10 w-10 mb-2" />
          <h2 className="text-xl font-bold" style={{ fontFamily: "Chivo" }}>
            🏆 {count} Work {count === 1 ? "Anniversary" : "Anniversaries"} this week!
          </h2>
          <p className="text-xs opacity-90 mt-1">Send a personal message — long-tenured staff appreciate it.</p>
        </div>
        <div className="overflow-y-auto flex-1">
          {rows.map(emp => <AnnivRow key={emp.id} emp={emp} />)}
        </div>
        <div className="p-3 bg-slate-50 border-t border-slate-200 text-center">
          <button onClick={onClose} className="text-xs text-slate-600 hover:text-amber-700" data-testid="anniv-modal-dismiss">Maybe later</button>
        </div>
      </div>
    </div>
  );
}
